import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchJson } from '../../../helpers/fetch.js';
import queryClient from '../../../../query_client.js';

import {
  option_set_hierarchy_nodes_path,
  option_set_hierarchy_update_option_sets_path,
  option_set_hierarchy_move_options_path
} from '~/routes';

export const useHierarchyBuilder = ({ hierarchyId }) => {
  const [rowData, setRowData] = useState([]);
  const ancestorCacheRef = useRef(new Map());

  const setNodesFromResponse = useCallback(response => {
    ancestorCacheRef.current.clear();
    setRowData(
      (response?.option_set_hierarchy_nodes ?? []).map(node => ({
        id: String(node.option_id),
        optionId: node.option_id,
        optionSetId: node.option_set_id,
        parentId: node.parent_option_id == null ? null : String(node.parent_option_id),
        name: node.name
      }))
    );
  }, []);

  const { data: hierarchyNodeData } = useQuery({
    queryKey: ['optionSetHierarchyNodes', hierarchyId],
    queryFn: () => fetchJson(option_set_hierarchy_nodes_path({ option_set_hierarchy_id: hierarchyId })),
    enabled: Boolean(hierarchyId)
  });

  useEffect(() => {
    if (hierarchyNodeData) setNodesFromResponse(hierarchyNodeData);
  }, [hierarchyNodeData, setNodesFromResponse]);

  const selectedOptionSetIds = useMemo(() => {
    if (!hierarchyNodeData?.option_set_hierarchy_nodes) return [];
    return [...new Set(hierarchyNodeData.option_set_hierarchy_nodes.map(node => node.option_set_id))];
  }, [hierarchyNodeData?.option_set_hierarchy_nodes])

  const updateOptionSetsMutation = useMutation({
    mutationFn: ({ optionSetId }) => fetchJson(option_set_hierarchy_update_option_sets_path({
      option_set_hierarchy_id: hierarchyId
    }), {
      method: 'POST',
      body: JSON.stringify({
        option_set_hierarchy_node: {
          option_set_ids: optionSetId
        }
      })
    }),
    onSuccess: (res) => {
      queryClient.invalidateQueries(['optionSetHierarchyNodes', hierarchyId])
    }
  });

  const moveOptionsMutation = useMutation({
    mutationFn: ({ optionIds, parentOptionId }) => fetchJson(option_set_hierarchy_move_options_path({
      option_set_hierarchy_id: hierarchyId
    }), {
      method: 'POST',
      body: JSON.stringify({
        option_set_hierarchy_node: {
          nodes: optionIds.map(optionId => ({
            option_id: optionId,
            parent_option_id: parentOptionId
          }))
        }
      })
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['optionSetHierarchyNodes', hierarchyId])
    }
  });

  const getAncestorIds = useCallback(row => {
    if (!row) return new Set();

    const rowId = String(row.id);

    if (ancestorCacheRef.current.has(rowId)) {
      return ancestorCacheRef.current.get(rowId);
    }

    const rowById = new Map(rowData.map(node => [String(node.id), node]));
    const ancestorIds = new Set();
    let parentId = row.parentId == null ? null : String(row.parentId);

    while (parentId != null) {
      if (ancestorIds.has(parentId)) break;

      ancestorIds.add(parentId);

      const parentRow = rowById.get(parentId);
      parentId = parentRow?.parentId == null ? null : String(parentRow.parentId);
    }

    ancestorCacheRef.current.set(rowId, ancestorIds);

    return ancestorIds;
  }, [rowData]);

  const isRowValidDropPosition = useCallback(event => {
    const draggedRow = event.source?.data;
    const targetRow = event.overNode?.data;

    if (!draggedRow) return false;
    if (!targetRow) return true;
    if (String(draggedRow.id) === String(targetRow.id)) return false;
    if (getAncestorIds(targetRow).has(String(draggedRow.id))) return false;

    return true;
  }, [getAncestorIds]);

  const handleRowDragEnd = useCallback(event => {
    if (!isRowValidDropPosition({ ...event, source: event.node })) {
      throw new Error('Invalid drop position');
    }

    const draggedNodes = event.node.isSelected() ? event.api.getSelectedNodes() : [event.node];

    const draggedOptionIds = draggedNodes.map(node => node.data.optionId);

    const targetRow = event.overNode?.data;
    const parentOptionId = event.overIndex === 0 ? null : targetRow?.optionId;

    moveOptionsMutation.mutate({
      optionIds: draggedOptionIds,
      parentOptionId
    });
  }, [moveOptionsMutation]);

  const handleOptionSetsChange = useCallback(optionSetId => {
    if (optionSetId.length < selectedOptionSetIds.length && !confirm(I18n.t('delete_hierarchy_option_sets_warning'))) {
      return;
    };
    updateOptionSetsMutation.mutate({
      optionSetId
    });
  }, [updateOptionSetsMutation, selectedOptionSetIds]);

  return {
    isSaving: moveOptionsMutation.isPending,
    rowData,
    selectedOptionSetIds,
    handleOptionSetsChange,
    handleRowDragEnd,
    isRowValidDropPosition
  };
};